import { isLevelValue } from '../../constants/tracking-groupings';
import { isDatePresetKey, DEFAULT_DATE_PRESET } from '../../utils/date-presets';
import { SORTABLE_FIELDS } from '../../utils/user-settings';
import type { FilterEntry } from '../../components/EntityFilterPicker';
import {
  DASHBOARD_SCHEMA_VERSION, uid, type Dashboard, type DashboardConfig, type DashboardPanel, type PanelViz,
} from './types';

const VIZ = new Set<PanelViz>(['table', 'line', 'bar', 'pie', 'stat', 'summary']);
const METRICS = new Set(SORTABLE_FIELDS.map((f) => f.value));
const isMetric = (m: unknown): m is string =>
  typeof m === 'string' && (METRICS.has(m) || /^ct_[a-z0-9_]{1,48}$/i.test(m));

// Mirrors the server's dashboardFilterKeys allowlist.
const FILTER_FIELDS = new Set([
  'offer_id', 'network_id', 'traffic_source_id', 'flow_id', 'landing_page_id',
  'ad_id', 'campaign_id', 'rule_group_id', 'country', 'external_id_type', 'event_type',
  'device', 'os', 'os_version', 'language', 'region', 'referrer_domain', 'hour', 'tags',
  'v1', 'v2', 'v3', 'v4', 'v5', 'v6', 'v7', 'v8', 'v9', 'v10',
]);

function clampInt(v: unknown, min: number, max: number, dflt: number): number {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : dflt;
}

function coerceFilters(raw: unknown): FilterEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: FilterEntry[] = [];
  for (const f of raw) {
    if (f && typeof f === 'object' && FILTER_FIELDS.has(f.field) && typeof f.value === 'string') {
      out.push({ field: f.field, value: f.value.slice(0, 256), label: typeof f.label === 'string' ? f.label.slice(0, 200) : f.value.slice(0, 200) });
    }
  }
  return out.slice(0, 50);
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function coercePanel(raw: any): DashboardPanel | null {
  if (!raw || typeof raw !== 'object') return null;
  const groupBy = isLevelValue(raw?.query?.group_by) ? raw.query.group_by : null;
  if (!groupBy) return null;
  const metrics: string[] = Array.isArray(raw?.query?.metrics) ? raw.query.metrics.filter(isMetric) : [];
  if (!metrics.length) return null;

  const filters = coerceFilters(raw?.query?.filters);
  const y2 = Array.isArray(raw?.query?.y2_metrics)
    ? raw.query.y2_metrics.filter((m: unknown) => isMetric(m) && metrics.includes(m as string)) : [];
  const wsId = typeof raw?.query?.workspace_id === 'string' && /^[0-9a-f-]{36}$/i.test(raw.query.workspace_id)
    ? raw.query.workspace_id : undefined;

  return {
    id: uid('p'), // regenerate so a pasted board never collides with an existing panel
    title: typeof raw.title === 'string' && raw.title.trim() ? raw.title.slice(0, 120) : 'Panel',
    subtitle: typeof raw.subtitle === 'string' && raw.subtitle.trim() ? raw.subtitle.slice(0, 200) : undefined,
    viz: VIZ.has(raw?.viz) ? raw.viz : 'table',
    query: {
      group_by: groupBy,
      metrics: metrics.slice(0, 20),
      ...(filters.length ? { filters } : {}),
      ...(y2.length ? { y2_metrics: y2 } : {}),
      sort_by: isMetric(raw?.query?.sort_by) ? raw.query.sort_by : undefined,
      sort_order: raw?.query?.sort_order === 'asc' ? 'asc' : raw?.query?.sort_order === 'desc' ? 'desc' : undefined,
      limit: raw?.query?.limit != null ? clampInt(raw.query.limit, 1, 1000, 50) : undefined,
      exclude_bots: typeof raw?.query?.exclude_bots === 'boolean' ? raw.query.exclude_bots : undefined,
      conv_time_basis: raw?.query?.conv_time_basis === 'click' ? 'click' : undefined,
      time_preset: isDatePresetKey(raw?.query?.time_preset) ? raw.query.time_preset : undefined,
      timezone: typeof raw?.query?.timezone === 'number' && Number.isInteger(raw.query.timezone)
        && raw.query.timezone >= -12 && raw.query.timezone <= 12 ? raw.query.timezone : undefined,
      workspace_id: wsId,
      compare: raw?.query?.compare === true ? true : undefined,
    },
    layout: {
      x: clampInt(raw?.layout?.x, 0, 11, 0),
      y: clampInt(raw?.layout?.y, 0, 5000, 0),
      w: clampInt(raw?.layout?.w, 1, 12, 6),
      h: clampInt(raw?.layout?.h, 1, 100, 8),
    },
  };
}

/** Normalise any (possibly older/foreign) board into { name, description, config }. */
export function parseImportedDashboard(text: string):
{ ok: true; name: string; description?: string; config: DashboardConfig } | { ok: false; error: string } {
  let raw: any;
  try { raw = JSON.parse(text); } catch { return { ok: false, error: 'That is not valid JSON.' }; }
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'This does not look like a dashboard.' };

  const panels = Array.isArray(raw.panels)
    ? (raw.panels.map(coercePanel).filter(Boolean) as DashboardPanel[]) : [];
  if (!panels.length) return { ok: false, error: 'No valid panels were found in the config.' };

  return {
    ok: true,
    name: typeof raw.name === 'string' && raw.name.trim() ? raw.name.slice(0, 120) : 'Imported dashboard',
    description: typeof raw.description === 'string' && raw.description.trim() ? raw.description.slice(0, 500) : undefined,
    config: {
      version: DASHBOARD_SCHEMA_VERSION,
      time_preset: isDatePresetKey(raw.time_preset) ? raw.time_preset : DEFAULT_DATE_PRESET,
      timezone: typeof raw.timezone === 'number' && Number.isInteger(raw.timezone)
        && raw.timezone >= -12 && raw.timezone <= 12 ? raw.timezone : undefined,
      panels: panels.slice(0, 50),
    },
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Shareable JSON — a portable board (no ids; the importer regenerates them). */
export function exportDashboard(board: Dashboard): string {
  return JSON.stringify({
    version: board.config.version,
    name: board.name,
    description: board.description ?? undefined,
    time_preset: board.config.time_preset,
    timezone: board.config.timezone,
    panels: board.config.panels.map((p) => ({ title: p.title, subtitle: p.subtitle, viz: p.viz, query: p.query, layout: p.layout })),
  }, null, 2);
}
